import { useState } from "react";
import Styles from "../../../styles/report/mobile/reportmobileexercises.module.scss";

const ClientReportExercisesMobile = ({ report }) => {
  const [currentBlock, setCurrentBlock] = useState(0);
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Dubai",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Map a unit to a short label we want to show
  const toUnitLabel = (u) => {
    const unit = String(u || "")
      .trim()
      .toLowerCase();
    switch (unit) {
      case "kg":
      case "kgs":
        return "kg";
      case "cal":
      case "cals":
      case "kcal":
        return "cals";

      default:
        return unit; // fallback to whatever is provided
    }
  };

  // Format a workout item’s absolute score with its unit
  const formatPrimaryScore = (workoutItem) => {
    const val = workoutItem?.scoreAchieved;
    const num = Number(val);
    if (!Number.isFinite(num)) return "";
    // show '-' when score is exactly 0
    if (num === 0) return "-";
    const rounded = Math.round(num);
    const unit = toUnitLabel(workoutItem?.unit || workoutItem?.workout?.unit);
    return unit ? `${rounded} ${unit}` : `${rounded}`;
  };

  const formatUnifiedScore = (workoutItem) => {
    const val = Number(workoutItem?.unifiedMetricScore);
    if (!Number.isFinite(val) || val === 0) return "-";
    return `${Math.round(val)} %`;
  };

  const sortedReports = Array.isArray(report)
    ? [...report].sort(
        (a, b) =>
          (a?.block ?? Number.MAX_SAFE_INTEGER) -
          (b?.block ?? Number.MAX_SAFE_INTEGER)
      )
    : [];

  const reportsByBlock = new Map(
    sortedReports
      .filter((r) => Number.isFinite(r?.block))
      .map((r) => [r.block, r])
  );

  const exerciseData = Array.from({ length: 4 }, (_, i) => {
    const block = i + 1;
    const r = reportsByBlock.get(block);
    const w = (idx) => r?.workouts?.[idx];
    return {
      id: block,
      date: formatDate(r?.workoutReportTestCreatedAt),
      label: w(0)?.scoreAchieved ? "Group Average" : "-",
      backSquat: formatPrimaryScore(w(0)),
      trapBar: formatPrimaryScore(w(1)),
      benchPress: formatPrimaryScore(w(2)),
      pendLay: formatPrimaryScore(w(3)),
      skiErg: formatPrimaryScore(w(4)),
      bikeErg: formatPrimaryScore(w(5)),
      backSquatAvg: formatUnifiedScore(w(0)),
      trapBarAvg: formatUnifiedScore(w(1)),
      benchPressAvg: formatUnifiedScore(w(2)),
      pendLayAvg: formatUnifiedScore(w(3)),
      skiErgAvg: formatUnifiedScore(w(4)),
      bikeErgAvg: formatUnifiedScore(w(5)),
    };
  });

  const handlePrevBlock = () => {
    setCurrentBlock((prev) => (prev > 0 ? prev - 1 : exerciseData.length - 1));
  };

  const handleNextBlock = () => {
    setCurrentBlock((prev) => (prev < exerciseData.length - 1 ? prev + 1 : 0));
  };

  const currentData = exerciseData[currentBlock];

  return (
    <>
      <h2 className={Styles.reportTitle}>EXERCISES REPORT</h2>
      <div className={Styles.exercisesReport}>
        <div className={Styles.blockHeader}>
          <span className={Styles.blockLabel}>Block</span>
          <div className={Styles.blockNavigation}>
            <button className={Styles.navButton} onClick={handlePrevBlock}>
              <span className={Styles.arrow}>‹</span>
            </button>
            <span className={Styles.blockNumber}>
              {String(currentBlock + 1).padStart(2, "0")}
            </span>
            <button className={Styles.navButton} onClick={handleNextBlock}>
              <span className={Styles.arrow}>›</span>
            </button>
          </div>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}></span>
          <span className={Styles.exerciseValue}>Absolute Score</span>
          <span className={Styles.exerciseGroupAvg}>TTR Score</span>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}>Rower ERG</span>
          <span className={Styles.exerciseValue}>
            {currentData?.skiErg || "-"}
          </span>
          <span className={Styles.exerciseGroupAvg}>
            {currentData?.skiErgAvg || "-"}
          </span>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}>Back Squat</span>
          <span className={Styles.exerciseValue}>
            {currentData?.backSquat || "-"}
          </span>
          <span className={Styles.exerciseGroupAvg}>
            {currentData?.backSquatAvg || "-"}
          </span>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}>Trap Bar</span>
          <span className={Styles.exerciseValue}>
            {currentData?.trapBar || "-"}
          </span>
          <span className={Styles.exerciseGroupAvg}>
            {currentData?.trapBarAvg || "-"}
          </span>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}>Bench Press</span>
          <span className={Styles.exerciseValue}>
            {currentData?.benchPress || "-"}
          </span>
          <span className={Styles.exerciseGroupAvg}>
            {currentData?.benchPressAvg || "-"}
          </span>
        </div>

        <div className={Styles.exerciseRow}>
          <span className={Styles.exerciseLabel}>Bent over Row</span>
          <span className={Styles.exerciseValue}>
            {currentData?.pendLay || "-"}
          </span>
          <span className={Styles.exerciseGroupAvg}>
            {currentData?.pendLayAvg || "-"}
          </span>
        </div>

        <div className={Styles.cellpadding}></div>
      </div>
    </>
  );
};

export default ClientReportExercisesMobile;
